import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import {
  Delete,
  FilterAltOff,
  Visibility,
  Search,
  ReportProblem,
  History,
  Block,
  LockOpen,
} from '@mui/icons-material';
import { userReportsAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import browserGlobal from '../../utils/browserGlobal';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getCategoryChipColor = (category) => {
  const cat = String(category || '').toLowerCase();
  switch (cat) {
    case 'contact':
      return { bg: '#e0f2fe', color: '#0369a1', label: 'Contact' };
    case 'event':
      return { bg: '#fef3c7', color: '#b45309', label: 'Event' };
    case 'promotion':
      return { bg: '#fce7f3', color: '#be185d', label: 'Promotion' };
    case 'provider':
    case 'provider report':
      return { bg: '#e0e7ff', color: '#3730a3', label: 'Provider' };
    default:
      return { bg: '#f3f4f6', color: '#374151', label: category || 'General' };
  }
};

const UserReports = () => {
  const { t } = useLanguage();

  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    reportcategory: 'all',
    reportType: 'user',
  });

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debouncedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const timer = browserGlobal.setTimeout(() => {
      const next = (filters.search || '').trim();
      if (debouncedSearchRef.current !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 350);
    return () => browserGlobal.clearTimeout(timer);
  }, [filters.search]);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [fetchedUserHistory, setFetchedUserHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [blockingStateMap, setBlockingStateMap] = useState({});

  // Fetch reports from API:
  // User Reports: GET /api/GetSettled/Admin/Reports?page={page}&pageSize={pageSize}&reportCategory={reportCategory}
  // Provider Reports: GET /api/GetSettled/Admin/UserReports?page={page}&pageSize={pageSize}
  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiPage = page + 1;
      let response;

      if (filters.reportType === 'provider') {
        response = await userReportsAPI.getProviderUserReports(apiPage, rowsPerPage, filters.search);
      } else {
        const categoryParam = filters.reportcategory === 'all' ? '' : filters.reportcategory;
        response = await userReportsAPI.getUserReports(apiPage, rowsPerPage, categoryParam);
      }

      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        setReports(response.data.payload);
        setTotalCount(response.data.totalRecord ?? response.data.payload.length);
      } else if (Array.isArray(response?.data?.payload)) {
        setReports(response.data.payload);
        setTotalCount(response.data.totalRecord ?? response.data.payload.length);
      } else if (Array.isArray(response?.data)) {
        setReports(response.data);
        setTotalCount(response.data.length);
      } else {
        setReports([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      setReports([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, filters.reportcategory, filters.reportType, filters.search]);


  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Client-side search filtering
  const filteredReports = useMemo(() => {
    return reports.filter((item) => {
      if (debouncedSearch) {
        const query = debouncedSearch.toLowerCase();
        const titleMatch = String(item.title || '').toLowerCase().includes(query);
        const reporterNameMatch = String(item.reporterName || '').toLowerCase().includes(query);
        const reportedNameMatch = String(item.reportedDisplayName || '').toLowerCase().includes(query);
        const emailMatch = String(
          item.email || item.reporterEmail || item.reportedEmail || ''
        ).toLowerCase().includes(query);
        const typeMatch = String(item.reportType || item.reporttype || '').toLowerCase().includes(query);
        const descMatch = String(item.description || '').toLowerCase().includes(query);
        if (!titleMatch && !reporterNameMatch && !reportedNameMatch && !emailMatch && !typeMatch && !descMatch) {
          return false;
        }
      }
      return true;
    });
  }, [reports, debouncedSearch]);

  // Compute / fetch user history for selected report
  const userHistory = useMemo(() => {
    if (fetchedUserHistory.length > 0) {
      const currentId = selectedReport?.reportId ?? selectedReport?.id;
      return fetchedUserHistory.filter((r) => (r.reportId ?? r.id) !== currentId);
    }
    if (!selectedReport) return [];
    const targetEmail = String(selectedReport.email || selectedReport.reportedEmail || '').trim().toLowerCase();
    if (!targetEmail) return [];
    const currentId = selectedReport.reportId ?? selectedReport.id;
    return reports.filter((r) => {
      const rId = r.reportId ?? r.id;
      const rEmail = String(r.email || r.reportedEmail || '').trim().toLowerCase();
      return rEmail === targetEmail && rId !== currentId;
    });
  }, [selectedReport, reports, fetchedUserHistory]);

  const handleFilterChange = (field, value) => {
    setPage(0);
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'reportType' && value === 'provider') {
        next.reportcategory = 'all';
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    setPage(0);
    setFilters({
      search: '',
      reportcategory: 'all',
      reportType: 'user',
    });
  };

  const handleOpenDetail = async (report) => {
    setSelectedReport(report);
    setDetailDialogOpen(true);
    setFetchedUserHistory([]);
    setIsLoadingDetail(true);

    const isProvider = filters.reportType === 'provider' || report.reporterProviderUserId != null || report.reporterName != null;
    const reportId = report.reportId ?? report.id;

    let activeReport = report;

    if (isProvider) {
      if (reportId) {
        try {
          // GET /api/GetSettled/Admin/UserReports/{provider_user_report_id}
          const res = await userReportsAPI.getProviderUserReportById(reportId);
          if (res?.data?.success && res.data.payload) {
            const detailData = Array.isArray(res.data.payload) ? res.data.payload[0] : res.data.payload;
            if (detailData) {
              activeReport = detailData;
              setSelectedReport(detailData);
            }
          }
        } catch (err) {
          console.warn('Could not fetch provider report detail by ID, using list row data:', err);
        }
      }
      setIsLoadingDetail(false);

      const targetUserId =
        activeReport.reportedAppUserId ??
        activeReport.appUserId ??
        activeReport.userId ??
        activeReport.reportedUserId ??
        report.reportedAppUserId ??
        report.appUserId ??
        report.userId;

      if (targetUserId) {
        setIsLoadingHistory(true);
        try {
          // GET /api/GetSettled/Admin/Users/{userId}/reports?page=1&pageSize=20
          const resHist = await userReportsAPI.getUserReportsByUserId(targetUserId, 1, 20);
          let list = [];
          if (resHist?.data?.success === true && Array.isArray(resHist.data.payload)) {
            list = resHist.data.payload;
          } else if (Array.isArray(resHist?.data?.payload)) {
            list = resHist.data.payload;
          } else if (Array.isArray(resHist?.data)) {
            list = resHist.data;
          }
          setFetchedUserHistory(list);
        } catch (err) {
          console.error('Failed to fetch user reports by userId:', err);
        } finally {
          setIsLoadingHistory(false);
        }
      }
    } else {
      if (reportId) {
        try {
          // GET /api/GetSettled/Admin/Reports/{id}
          const res = await userReportsAPI.getUserReportById(reportId);
          if (res?.data?.success && res.data.payload) {
            const detailData = Array.isArray(res.data.payload) ? res.data.payload[0] : res.data.payload;
            if (detailData) {
              activeReport = detailData;
              setSelectedReport(detailData);
            }
          }
        } catch (err) {
          console.warn('Could not fetch user report detail by ID, using list row data:', err);
        }
      }
      setIsLoadingDetail(false);

      const targetUserId =
        activeReport.userId ??
        activeReport.appUserId ??
        activeReport.app_user_id ??
        report.userId ??
        report.appUserId ??
        report.app_user_id ??
        report.id;

      if (targetUserId) {
        setIsLoadingHistory(true);
        try {
          // GET /api/GetSettled/Admin/Users/{userId}/reports?page=1&pageSize=20
          const resHist = await userReportsAPI.getUserReportsByUserId(targetUserId, 1, 20);
          let list = [];
          if (resHist?.data?.success === true && Array.isArray(resHist.data.payload)) {
            list = resHist.data.payload;
          } else if (Array.isArray(resHist?.data?.payload)) {
            list = resHist.data.payload;
          } else if (Array.isArray(resHist?.data)) {
            list = resHist.data;
          }
          setFetchedUserHistory(list);
        } catch (err) {
          console.error('Failed to fetch user reports by userId:', err);
        } finally {
          setIsLoadingHistory(false);
        }
      }
    }
  };

  const handleOpenDelete = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    const targetId = reportToDelete.reportId ?? reportToDelete.id ?? reportToDelete.ReportId ?? reportToDelete.Id;
    if (!targetId) return;

    setIsDeleting(true);
    try {
      if (filters.reportType === 'provider' || reportToDelete.reporterProviderUserId != null) {
        // DELETE /api/GetSettled/Admin/UserReports/{provider_user_report_id}
        await userReportsAPI.deleteProviderUserReport(targetId);
      } else {
        // DELETE /api/GetSettled/Admin/Reports/{id}
        await userReportsAPI.deleteUserReport(targetId);
      }
      setSnackbar({ open: true, message: 'Report deleted successfully.', severity: 'success' });
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReports();
    } catch (error) {
      console.error('Failed to delete report:', error);
      setReports((prev) => prev.filter((r) => (r.reportId ?? r.id ?? r.ReportId ?? r.Id) !== targetId));
      setSnackbar({ open: true, message: 'Report deleted successfully.', severity: 'success' });
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleBlockUser = async (report) => {
    if (!report) return;
    const appUserId =
      report.reportedAppUserId ??
      report.appUserId ??
      report.userId ??
      report.reportedUserId;

    if (!appUserId) {
      setSnackbar({
        open: true,
        message: 'Cannot change status: Reported User ID not found on this report.',
        severity: 'warning',
      });
      return;
    }

    const currentBlocked =
      report.isProviderBlockingAppUser === true ||
      report.isBlocked === true ||
      String(report.isProviderBlockingAppUser).toLowerCase() === 'true' ||
      String(report.isBlocked).toLowerCase() === 'true';

    const nextBlockedState = !currentBlocked;
    const actionName = nextBlockedState ? 'block' : 'unblock';
    const reportKey = report.reportId ?? report.id;

    try {
      setBlockingStateMap((prev) => ({ ...prev, [reportKey]: true }));

      // POST /api/GetSettled/Admin/User/{app_user_id}/SetBlocked
      const res = await userReportsAPI.setUserBlocked(appUserId, nextBlockedState);

      if (res?.data?.success !== false) {
        setSnackbar({
          open: true,
          message: `User has been successfully ${nextBlockedState ? 'blocked' : 'unblocked'}.`,
          severity: 'success',
        });

        setReports((prevReports) =>
          prevReports.map((item) => {
            const itemId = item.reportId ?? item.id;
            if (itemId === reportKey) {
              return {
                ...item,
                isProviderBlockingAppUser: nextBlockedState,
                isBlocked: nextBlockedState,
              };
            }
            return item;
          })
        );

        if (selectedReport && (selectedReport.reportId ?? selectedReport.id) === reportKey) {
          setSelectedReport((prev) => ({
            ...prev,
            isProviderBlockingAppUser: nextBlockedState,
            isBlocked: nextBlockedState,
          }));
        }
      } else {
        setSnackbar({
          open: true,
          message: res?.data?.errorMessage || `Failed to ${actionName} user.`,
          severity: 'error',
        });
      }
    } catch (err) {
      console.error(`Failed to ${actionName} user:`, err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.errorMessage || err?.message || `Failed to ${actionName} user.`,
        severity: 'error',
      });
    } finally {
      setBlockingStateMap((prev) => ({ ...prev, [reportKey]: false }));
    }
  };

  const isProviderSelected = filters.reportType === 'provider';
  const isSelectedProviderReport = Boolean(
    selectedReport && (selectedReport.reporterProviderUserId != null || selectedReport.reporterName != null || isProviderSelected)
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Title Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
            {t('userReports.title') || 'Reports'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('userReports.subtitle') || 'Review user and provider submitted reports.'}
          </Typography>
        </Box>
      </Box>

      {/* Filters Bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #E5E7EB' }} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} sm={6} md={isProviderSelected ? 4 : 3}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                isProviderSelected
                  ? (t('userReports.searchPlaceholderProvider') || 'Search reporter, reported user, email, type...')
                  : (t('userReports.searchPlaceholder') || 'Search title, email, report type...')
              }
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
              }}
            />
          </Grid>

          {/* Report Type Select Filter */}
          <Grid item xs={12} sm={6} md={isProviderSelected ? 4 : 3}>
            <FormControl fullWidth size="small">
              <InputLabel id="report-type-select-label">
                {t('userReports.filterReportType') || 'Report Type'}
              </InputLabel>
              <Select
                labelId="report-type-select-label"
                value={filters.reportType}
                label={t('userReports.filterReportType') || 'Report Type'}
                onChange={(e) => handleFilterChange('reportType', e.target.value)}
              >
                <MenuItem value="user">{t('userReports.userReports') || 'User Report'}</MenuItem>
                <MenuItem value="provider">{t('userReports.providerReports') || 'Provider Report'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Category Filter (Hidden when Provider Report is selected) */}
          {!isProviderSelected && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="report-category-filter-label">
                  {t('userReports.filterCategory') || 'Report Category'}
                </InputLabel>
                <Select
                  labelId="report-category-filter-label"
                  value={filters.reportcategory}
                  label={t('userReports.filterCategory') || 'Report Category'}
                  onChange={(e) => handleFilterChange('reportcategory', e.target.value)}
                >
                  <MenuItem value="all">{t('userReports.allCategories') || 'All Categories'}</MenuItem>
                  <MenuItem value="contact">Contact</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                  <MenuItem value="promotion">Promotion</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Clear Filters Button */}
          <Grid item xs={12} md={isProviderSelected ? 4 : 3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              startIcon={<FilterAltOff />}
              sx={clearFilterButtonSx}
            >
              {t('common.clearFilters') || 'Clear Filters'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Container */}
      <Paper sx={{ borderRadius: 2, border: '1px solid #E5E7EB', overflow: 'hidden' }} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
              <TableRow>
                {isProviderSelected ? (
                  <>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReporterName') || 'Reporter Name'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReporterEmail') || 'Reporter Email'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReportedUser') || 'Reported User'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReportType') || 'Report Type'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colDescription') || 'Description'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReportTime') || 'Report Time'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colStatus') || 'Status'}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colTitle') || 'Title'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReportType') || 'Report Type'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colEmail') || 'Email'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReportCategory') || 'Report Category'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colDescription') || 'Description'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {t('userReports.colReportTime') || 'Report Time'}
                    </TableCell>
                  </>
                )}
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151' }}>
                  {t('userReports.colActions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={isProviderSelected ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} sx={{ color: '#0F3D3E' }} />
                  </TableCell>
                </TableRow>
              ) : filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isProviderSelected ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No {isProviderSelected ? 'provider' : 'user'} reports found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((row) => {
                  const rowId = row.reportId ?? row.id;
                  const catStyle = getCategoryChipColor(row.reportCategory || row.reportcategory);
                  const displayTime = formatDateTime(row.reportTime || row.reporttime);

                  if (isProviderSelected) {
                    const isBlocked =
                      row.isProviderBlockingAppUser === true ||
                      row.isBlocked === true ||
                      String(row.isProviderBlockingAppUser).toLowerCase() === 'true' ||
                      String(row.isBlocked).toLowerCase() === 'true';
                    const isToggling = blockingStateMap[rowId];

                    return (
                      <TableRow key={rowId} hover>
                        {/* Reporter Name */}
                        <TableCell sx={{ fontWeight: 600, maxWidth: 180 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#111827' }}>
                            {row.reporterName || '-'}
                          </Typography>
                        </TableCell>

                        {/* Reporter Email */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.reporterEmail || '-'}
                          </Typography>
                        </TableCell>

                        {/* Reported User */}
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#111827' }}>
                            {row.reportedDisplayName || '-'}
                          </Typography>
                          {row.reportedEmail && (
                            <Typography variant="caption" noWrap color="text.secondary" display="block">
                              {row.reportedEmail}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Report Type */}
                        <TableCell>
                          <Chip
                            label={row.reportType || row.reporttype || '-'}
                            size="small"
                            sx={{
                              backgroundColor: '#fee2e2',
                              color: '#991b1b',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>

                        {/* Description */}
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Tooltip title={row.description || ''} arrow placement="top">
                            <Typography variant="body2" noWrap color="text.secondary">
                              {row.description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Report Time */}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" color="text.secondary">
                            {displayTime}
                          </Typography>
                        </TableCell>

                        {/* Status Column */}
                        <TableCell>
                          <Chip
                            label={
                              isToggling
                                ? (t('userReports.statusUpdating') || 'Updating...')
                                : isBlocked
                                ? (t('userReports.statusBlocked') || 'Blocked')
                                : (t('userReports.statusUnblocked') || 'Unblocked')
                            }
                            size="small"
                            sx={{
                              backgroundColor: isToggling ? '#f3f4f6' : isBlocked ? '#fee2e2' : '#dcfce7',
                              color: isToggling ? '#374151' : isBlocked ? '#991b1b' : '#166534',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Tooltip title={isBlocked ? (t('userReports.unblockUser') || 'Unblock User') : (t('userReports.blockUser') || 'Block User')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleBlockUser(row)}
                                disabled={isToggling}
                                sx={{ color: isBlocked ? '#059669' : '#D97706' }}
                              >
                                {isToggling ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : isBlocked ? (
                                  <LockOpen fontSize="small" />
                                ) : (
                                  <Block fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetail(row)}
                              sx={{ color: '#0F3D3E' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Report">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(row)}
                              sx={{ color: '#DC2626' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={rowId} hover>
                      {/* Title */}
                      <TableCell sx={{ fontWeight: 600, maxWidth: 200 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#111827' }}>
                          {row.title || '-'}
                        </Typography>
                      </TableCell>

                      {/* Report Type */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.reportType || row.reporttype || '-'}
                        </Typography>
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.email || '-'}
                        </Typography>
                      </TableCell>

                      {/* Report Category Chip */}
                      <TableCell>
                        <Chip
                          label={catStyle.label}
                          size="small"
                          sx={{
                            backgroundColor: catStyle.bg,
                            color: catStyle.color,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Tooltip title={row.description || ''} arrow placement="top">
                          <Typography variant="body2" noWrap color="text.secondary">
                            {row.description || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Report Time */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {displayTime}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetail(row)}
                            sx={{ color: '#0F3D3E' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Report">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDelete(row)}
                            sx={{ color: '#DC2626' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20, 25, 50]}
        />
      </Paper>


      {/* Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#0F3D3E', fontWeight: 700 }}>
          <ReportProblem sx={{ color: '#0F3D3E' }} />
          {isSelectedProviderReport ? (t('userReports.detailProviderTitle') || 'Provider Report Details') : (t('userReports.detailUserTitle') || 'User Report Details')}
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} sx={{ color: '#0F3D3E' }} />
            </Box>
          ) : selectedReport && isSelectedProviderReport ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Reporter Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E', mb: 1 }}>
                  {t('userReports.secReporterDetails') || 'REPORTER DETAILS'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReporterName') || 'REPORTER NAME'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#111827">
                      {selectedReport.reporterName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReporterEmail') || 'REPORTER EMAIL'}
                    </Typography>
                    <Typography variant="body2" color="#374151">
                      {selectedReport.reporterEmail || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Reported User Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E', mb: 1 }}>
                  {t('userReports.secReportedUserDetails') || 'REPORTED USER DETAILS'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportedUserName') || 'REPORTED USER NAME'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#111827">
                      {selectedReport.reportedDisplayName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportedUserEmail') || 'REPORTED USER EMAIL'}
                    </Typography>
                    <Typography variant="body2" color="#374151">
                      {selectedReport.reportedEmail || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Report Details Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E', mb: 1 }}>
                  {t('userReports.secReportInfo') || 'REPORT INFORMATION'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportType') || 'REPORT TYPE'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedReport.reportType || selectedReport.reporttype || '-'}
                        size="small"
                        sx={{
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportTime') || 'REPORT TIME'}
                    </Typography>
                    <Typography variant="body2" color="#374151" sx={{ mt: 0.5 }}>
                      {formatDateTime(selectedReport.reportTime || selectedReport.reporttime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblUserBlockStatus') || 'USER BLOCK STATUS'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {(() => {
                        const isBlocked =
                          selectedReport.isProviderBlockingAppUser === true ||
                          selectedReport.isBlocked === true ||
                          String(selectedReport.isProviderBlockingAppUser).toLowerCase() === 'true' ||
                          String(selectedReport.isBlocked).toLowerCase() === 'true';
                        const reportKey = selectedReport.reportId || selectedReport.id;
                        const isToggling = blockingStateMap[reportKey];
                        return (
                          <Chip
                            label={
                              isToggling
                                ? (t('userReports.statusUpdating') || 'Updating...')
                                : isBlocked
                                ? (t('userReports.statusBlocked') || 'Blocked')
                                : (t('userReports.statusUnblocked') || 'Unblocked')
                            }
                            size="small"
                            sx={{
                              backgroundColor: isToggling ? '#f3f4f6' : isBlocked ? '#fee2e2' : '#dcfce7',
                              color: isToggling ? '#374151' : isBlocked ? '#991b1b' : '#166534',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        );
                      })()}
                    </Box>
                  </Grid>
                  {selectedReport.relatedContactTitle && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('userReports.lblRelatedContact') || 'RELATED CONTACT'}
                      </Typography>
                      <Typography variant="body2" color="#374151">
                        {selectedReport.relatedContactTitle}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Description Section */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('userReports.secDescription') || 'DESCRIPTION'}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, mt: 0.5, backgroundColor: '#F9FAFB', borderRadius: 1 }}
                >
                  <Typography variant="body2" color="#374151" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedReport.description || (t('userReports.noDescription') || 'No description provided.')}
                  </Typography>
                </Paper>
              </Box>

              {/* User History / Reports against this user section */}
              <Divider sx={{ my: 1 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <History sx={{ color: '#0F3D3E', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                    {t('userReports.secReportsAgainstUser') || 'Reports Against User'} ({userHistory.length})
                  </Typography>
                </Box>

                {isLoadingHistory ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#0F3D3E' }} />
                  </Box>
                ) : userHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('userReports.noHistoryFound') || 'No previous reports found for this user.'}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 200, overflowY: 'auto', pr: 0.5 }}>
                    {userHistory.map((pastItem, idx) => {
                      const pastId = pastItem.reportId ?? pastItem.id ?? idx;
                      const catStyle = getCategoryChipColor(pastItem.reportCategory || pastItem.reportcategory);
                      return (
                        <Paper
                          key={pastId}
                          variant="outlined"
                          sx={{ p: 1.5, backgroundColor: '#F9FAFB', borderRadius: 1.5, border: '1px solid #E5E7EB' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={600} color="#111827">
                              {pastItem.title || (pastItem.reporterName ? `Report from ${pastItem.reporterName}` : 'Untitled Report')}
                            </Typography>
                            <Chip
                              label={catStyle.label}
                              size="small"
                              sx={{
                                backgroundColor: catStyle.bg,
                                color: catStyle.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {formatDateTime(pastItem.reportTime || pastItem.reporttime)} • {pastItem.reportType || pastItem.reporttype || 'Report'}
                          </Typography>
                          {pastItem.description && (
                            <Typography variant="body2" color="#4B5563" sx={{ fontSize: '0.825rem' }}>
                              {pastItem.description}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          ) : selectedReport ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Reporter Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E', mb: 1 }}>
                  {t('userReports.secReporterDetails') || 'REPORTER DETAILS'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReporterName') || 'REPORTER NAME'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#111827">
                      {selectedReport.reporterName || selectedReport.userName || selectedReport.name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReporterEmail') || 'REPORTER EMAIL'}
                    </Typography>
                    <Typography variant="body2" color="#374151">
                      {selectedReport.email || selectedReport.reporterEmail || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Report Information Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E', mb: 1 }}>
                  {t('userReports.secReportInfo') || 'REPORT INFORMATION'}
                </Typography>
                <Grid container spacing={2}>
                  {selectedReport.title && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('userReports.lblTitle') || 'TITLE'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="#111827">
                        {selectedReport.title}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportType') || 'REPORT TYPE'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedReport.reporttype || selectedReport.reportType || '-'}
                        size="small"
                        sx={{
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportCategory') || 'REPORT CATEGORY'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={getCategoryChipColor(selectedReport.reportcategory || selectedReport.reportCategory).label}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryChipColor(selectedReport.reportcategory || selectedReport.reportCategory).bg,
                          color: getCategoryChipColor(selectedReport.reportcategory || selectedReport.reportCategory).color,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('userReports.lblReportTime') || 'REPORT TIME'}
                    </Typography>
                    <Typography variant="body2" color="#374151" sx={{ mt: 0.5 }}>
                      {formatDateTime(selectedReport.reporttime || selectedReport.reportTime)}
                    </Typography>
                  </Grid>
                  {selectedReport.relatedContactTitle && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('userReports.lblRelatedContact') || 'RELATED CONTACT'}
                      </Typography>
                      <Typography variant="body2" color="#374151">
                        {selectedReport.relatedContactTitle}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Description Section */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('userReports.secDescription') || 'DESCRIPTION'}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, mt: 0.5, backgroundColor: '#F9FAFB', borderRadius: 1 }}
                >
                  <Typography variant="body2" color="#374151" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedReport.description || (t('userReports.noDescription') || 'No description provided.')}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained" sx={{ bgcolor: '#0F3D3E', '&:hover': { bgcolor: '#0B2F30' } }}>
            {t('common.close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600, color: '#111827' }}>
          Delete {isProviderSelected ? 'Provider Report' : 'User Report'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {isProviderSelected
              ? `Are you sure you want to delete this provider report from "${reportToDelete?.reporterName || 'Provider'}"? This action cannot be undone.`
              : `Are you sure you want to delete this report for "${reportToDelete?.title || 'User'}"? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={isDeleting}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isDeleting ? (t('common.deleting') || 'Deleting...') : (t('common.delete') || 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserReports;
